import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { ROW_CAP } from '../../GscTools/query';
import { formatPeriod } from '../../GscTools/period';
import { contributorActionColumn, contributorTableColumns } from '../keys';
import { CONTRIBUTORS } from '../scope';

/**
 * Block 04 — the queries or the pages Search Console returned under the active topic's filters.
 *
 * Two views of one topic, and two different aggregations. Queries are aggregated by property, so
 * a query's clicks are counted once however many of the site's pages it showed; pages are
 * aggregated by page, so the same search can count on two page rows. The switch therefore says
 * which aggregation it is on, and nothing is carried from one view's totals into the other's —
 * they are not expected to agree, and forcing them to would invent a reconciliation.
 *
 * Evidence, not an inventory. Search Console withholds rows below its own thresholds and the
 * retrieval is bounded, so a short list never means a small topic and an empty one never proves
 * the topic matched nothing. A query names the other topics it also meets, checked in the browser
 * and never acted on.
 *
 * Every row carries its exact key into the next tool: a query opens its page contributors under a
 * `query equals` rule, a page opens the topic's queries on it or fills URL Inspection without
 * inspecting it.
 */
class TopicContributors extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.additionalBox = this.additionalBox.bind(this);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    /** Queries / Pages, as the table-filter control, with the aggregation each one is on. */
    additionalBox() {
        const { t, view, status, onView } = this.props;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <div className="table-additional-view-btn-groups">
                        {Object.values(CONTRIBUTORS).map((value) => (
                            <button
                                type="button"
                                key={value}
                                className={view === value ? 'active' : ''}
                                aria-pressed={view === value}
                                disabled={status === 'loading'}
                                onClick={() => onView(value)}
                            >
                                {t(`tools.topic_clusters.contributors.views.${value}`)}
                            </button>
                        ))}
                    </div>
                </div>

                <span className="gsc-tool-field-note">
                    {t(`tools.topic_clusters.contributors.aggregation.${view}`)}
                </span>
            </div>
        );
    }

    render() {
        const {
            t,
            scope,
            topic,
            view,
            period,
            shortened,
            status,
            error,
            rows,
            stopReason,
            loadingMore,
            onLoadMore,
            onExport,
        } = this.props;

        const columns = contributorTableColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            view,
        });
        const tableColumns = [
            ...columns,
            contributorActionColumn(t, {
                scope,
                period,
                topic: scope.topics.find((item) => item.name === topic),
                view,
            }),
        ];
        const overlapping = rows.filter((row) => row.alsoIn?.length).length;

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            04 · {t(`tools.topic_clusters.contributors.title.${view}`)}
                        </p>
                        <p className="gsc-tool-sub">
                            {topic
                                ? `${topic} · ${formatPeriod(period)}`
                                : t('tools.topic_clusters.contributors.description')}
                        </p>
                    </div>

                    {!!topic && (
                        <div className="gsc-tool-icon-actions">
                            <button
                                type="button"
                                title={t('tools.topic_clusters.contributors.export')}
                                aria-label={t('tools.topic_clusters.contributors.export')}
                                onClick={onExport}
                                disabled={status !== 'done' || !rows.length}
                            >
                                <i className="icon-fi_download" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="card-body">
                    {!topic && (
                        <div className="gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-keywords" />
                            </span>
                            <h4>{t('tools.topic_clusters.contributors.idle_title')}</h4>
                            <p>{t('tools.topic_clusters.contributors.idle')}</p>
                        </div>
                    )}

                    {/* Said before the rows: a reader comparing this block with the totals above
                        has to know the windows differ before they conclude the numbers do. */}
                    {!!topic && shortened && (
                        <div className="gsc-tool-banner">
                            {t('tools.topic_clusters.contributors.shortened', {
                                period: formatPeriod(period),
                            })}
                        </div>
                    )}

                    {!!topic && status === 'loading' && <Loading padding={20} />}

                    {!!topic && status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {!!topic && status === 'done' && (
                        <>
                            {/* A failure while loading more keeps the rows already retrieved. */}
                            {!!error && <div className="gsc-tool-banner error">{error}</div>}

                            {!!overlapping && (
                                <div className="gsc-tool-banner">
                                    {t('tools.topic_clusters.contributors.overlap', {
                                        overlapping,
                                        rows: rows.length,
                                    })}
                                </div>
                            )}

                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                searchFunction={this.searchFunction}
                                additionalBox={this.additionalBox}
                                fileName={t(`tools.topic_clusters.contributors.file.${view}`, {
                                    topic,
                                })}
                                customEmptyMessage={t(
                                    `tools.topic_clusters.contributors.empty.${view}`,
                                )}
                                customContainerStyles="gsc-tool-key-table"
                                loadMore={onLoadMore}
                                loadMoreLoading={loadingMore}
                                showLoadMoreBtn={stopReason === 'more_available'}
                            />

                            <p className="gsc-tool-table-note">
                                {t(`tools.gsc.stop.${stopReason}`)}{' '}
                                {t('tools.topic_clusters.contributors.retrieved', {
                                    rows: rows.length,
                                    cap: ROW_CAP,
                                })}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.topic_clusters.contributors.not_inventory')}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t(`tools.topic_clusters.contributors.links_note.${view}`)}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(TopicContributors);
